import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getZReport, exportZReportToExcel } from "../../components/Api/GmsZReport";
import { getZReportData } from "../../components/Api/GmsDayClosing";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { exportDailyClosingToExcel } from "../../components/Api/GmsDayClosing";
import { Download, Printer } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import html2pdf from "html2pdf.js";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";

// Custom styles for Select components (from PayrollProcessingPage)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    businessId: authUser.companyId || user.user_Company_Id || localStorage.getItem("userCompanyId") || null,
    branchId: authUser.branchId || user.user_Branch_Id || localStorage.getItem("userBranchId") || null,
    companyId: authUser.companyId || user.user_Company_Id || localStorage.getItem("userCompanyId") || null,
  };
};

export default function ZReportPage() {
  console.log("🎬 [ZReport] Component rendering");

  const [dayClosings, setDayClosings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [holidayFilter, setHolidayFilter] = useState(null); // null = All, false = Regular, true = Holiday
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  console.log("📊 [ZReport] Current state:", {
    dayClosingsCount: dayClosings.length,
    loading: loading,
    selectedBusiness: selectedBusiness?.value,
    selectedBranch: selectedBranch?.value,
    fromDate: fromDate,
    toDate: toDate,
    totalCount: totalCount
  });

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsZReport');

  // Get user session
  const session = useMemo(() => getUserSession(), []);
  const userBusinessId = session.businessId;
  const userBranchId = session.branchId;
  const userCompanyId = session.companyId;

  // Load businesses
  const loadBusinesses = useCallback(async () => {
    console.log("🏢 [ZReport] Loading businesses...");
    try {
      const data = await getBusinesses({ business_Active: true });
      console.log("🏢 [ZReport] Businesses loaded:", data?.length || 0);
      setBusinesses(data || []);

      // Auto-select business from session if available
      if (userBusinessId && data) {
        console.log("🏢 [ZReport] Auto-selecting business from session:", userBusinessId);
        const business = data.find(b => b.business_Id === userBusinessId);
        if (business) {
          console.log("✅ [ZReport] Business found and selected:", business.business_NameEn || business.business_Name);
          setSelectedBusiness({
            value: business.business_Id,
            label: business.business_NameEn || business.business_Name
          });
        } else {
          console.log("⚠️ [ZReport] Business not found in list");
        }
      } else {
        console.log("⚠️ [ZReport] No userBusinessId or no data, skipping auto-select");
      }
    } catch (error) {
      console.error("❌ [ZReport] Error loading businesses:", error);
    }
  }, [userBusinessId]);

  // Load branches
  const loadBranches = useCallback(async (businessId) => {
    console.log("🏪 [ZReport] Loading branches for businessId:", businessId);
    try {
      if (!businessId) {
        console.log("⚠️ [ZReport] No businessId, clearing branches");
        setBranches([]);
        setSelectedBranch(null);
        return;
      }

      const data = await getBranches({ businessId: businessId, branch_Active: true });
      console.log("🏪 [ZReport] Branches loaded:", data?.length || 0);
      setBranches(data || []);

      // Auto-select branch from session if available
      if (userBranchId && data) {
        console.log("🏪 [ZReport] Auto-selecting branch from session:", userBranchId);
        const branch = data.find(b => b.branch_Id === userBranchId);
        if (branch) {
          console.log("✅ [ZReport] Branch found and selected:", branch.branch_NameEn);
          setSelectedBranch({
            value: branch.branch_Id,
            label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
          });
        } else {
          console.log("⚠️ [ZReport] Branch not found in list");
        }
      } else {
        console.log("⚠️ [ZReport] No userBranchId or no data, skipping auto-select");
      }
    } catch (error) {
      console.error("❌ [ZReport] Error loading branches:", error);
      setBranches([]);
    }
  }, [userBranchId]);

  // Load Z Report data
  const loadZReportData = useCallback(async () => {
    console.log("🔍 [ZReport] loadZReportData called");
    console.log("🔍 [ZReport] selectedBusiness:", selectedBusiness);
    console.log("🔍 [ZReport] selectedBranch:", selectedBranch);

    // Require branch to be selected before loading data
    if (!selectedBranch) {
      console.log("⚠️ [ZReport] No branch selected, clearing data");
      setDayClosings([]);
      setTotalCount(0);
      return;
    }

    if (!selectedBusiness) {
      console.log("⚠️ [ZReport] No business selected, clearing data");
      setDayClosings([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const filter = {
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        fromDate: fromDate ? formatDateForAPI(fromDate, false) : null,
        toDate: toDate ? formatDateForAPI(toDate, false) : null,
        holiday: holidayFilter,
        page: page,
        pageSize: pageSize
      };

      console.log("📤 [ZReport] Sending filter to API:", JSON.stringify(filter, null, 2));
      console.log("📤 [ZReport] Filter details:", {
        businessId: filter.businessId,
        branchId: filter.branchId,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        holiday: filter.holiday,
        page: filter.page,
        pageSize: filter.pageSize
      });

      const response = await getZReport(filter);

      console.log("📥 [ZReport] API Response received:", response);
      console.log("📥 [ZReport] Response type:", typeof response);
      console.log("📥 [ZReport] Response keys:", response ? Object.keys(response) : "null/undefined");
      console.log("📥 [ZReport] dayClosings:", response?.dayClosings);
      console.log("📥 [ZReport] dayClosings length:", response?.dayClosings?.length);
      console.log("📥 [ZReport] totalCount:", response?.totalCount);

      const dayClosingsData = response.dayClosings || [];
      const totalCountData = response.totalCount || 0;

      console.log("✅ [ZReport] Setting state - dayClosings:", dayClosingsData.length, "totalCount:", totalCountData);

      setDayClosings(dayClosingsData);
      setTotalCount(totalCountData);

      console.log("✅ [ZReport] State updated successfully");
    } catch (error) {
      console.error("❌ [ZReport] Error loading Z Report:", error);
      console.error("❌ [ZReport] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      Swal.fire("Error", "Failed to load Z Report data: " + (error.response?.data?.message || error.message), "error");
      setDayClosings([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      console.log("🏁 [ZReport] loadZReportData completed, loading set to false");
    }
  }, [selectedBusiness, selectedBranch, fromDate, toDate, holidayFilter, page, pageSize]);

  // Initial load
  useEffect(() => {
    console.log("🚀 [ZReport] Component mounted/initialized");
    console.log("🚀 [ZReport] hasPermission:", hasPermission);

    if (hasPermission) {
      console.log("✅ [ZReport] Permission granted, loading businesses");
      loadBusinesses();
    } else {
      console.log("❌ [ZReport] No permission, redirecting to Dashboard");
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, loadBusinesses]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness, loadBranches]);

  // Load data when filters change - only after branch is selected
  useEffect(() => {
    console.log("🔄 [ZReport] Filter change effect triggered");
    console.log("🔄 [ZReport] Conditions:", {
      selectedBusiness: !!selectedBusiness,
      selectedBranch: !!selectedBranch,
      hasPermission: hasPermission
    });

    // Only load data if branch is selected (either from dropdown or from user session)
    if (selectedBranch && selectedBusiness && hasPermission) {
      console.log("✅ [ZReport] Conditions met, calling loadZReportData");
      loadZReportData();
    } else {
      console.log("⚠️ [ZReport] Conditions not met, skipping loadZReportData");
      if (!selectedBranch) {
        setDayClosings([]);
        setTotalCount(0);
      }
    }
  }, [selectedBusiness, selectedBranch, fromDate, toDate, holidayFilter, page, pageSize, loadZReportData, hasPermission]);

  // Log state changes for debugging
  useEffect(() => {
    console.log("📊 [ZReport] State update - dayClosings:", dayClosings.length, "totalCount:", totalCount, "loading:", loading);
    if (dayClosings.length > 0) {
      console.log("📊 [ZReport] First dayClosing item:", dayClosings[0]);
    }
  }, [dayClosings, totalCount, loading]);

  // Business options
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  // Branch options
  const branchOptions = useMemo(() => {
    return branches.map(b => ({
      value: b.branch_Id,
      label: `${b.branch_NameEn} - ${b.branch_NameAr}`
    }));
  }, [branches]);

  // Holiday filter options
  const holidayOptions = [
    { value: null, label: 'All' },
    { value: false, label: 'Regular Closing' },
    { value: true, label: 'Holiday' }
  ];

  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      if (!selectedBranch) {
        Swal.fire("Warning", "Please select a branch before exporting", "warning");
        return;
      }

      setLoading(true);
      const filter = {
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        fromDate: fromDate ? formatDateForAPI(fromDate, false) : null,
        toDate: toDate ? formatDateForAPI(toDate, false) : null,
        holiday: holidayFilter
      };

      await exportZReportToExcel(filter);
      Swal.fire("Success", "Z Report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting Z Report:", error);
      Swal.fire("Error", "Failed to export Z Report: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Print Z Report (for current date - top button)
  const [zReportData, setZReportData] = useState(null);
  const [showZReportModal, setShowZReportModal] = useState(false);
  const zReportPrintRef = useRef(null);

  const handlePrintZReport = async () => {
    if (!selectedBusiness || !selectedBranch) {
      Swal.fire("Error", "Please select company and branch", "error");
      return;
    }

    try {
      setLoading(true);

      // Check if day closing is done for current date
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const checkRequest = {
        transactionDate: formatDateForAPI(today, false),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      if (!dayClosingCheck.isClosed || dayClosingCheck.isHoliday) {
        Swal.fire("Error", "Day closing not done for the day. Please close and try again.", "error");
        setLoading(false);
        return;
      }

      // Get business and branch names from selected options
      const businessName = selectedBusiness.label || businesses.find(b => b.business_Id === selectedBusiness.value)?.business_NameEn || "";
      const branchName = selectedBranch.label || branches.find(b => b.branch_Id === selectedBranch.value)?.branch_NameEn || "";
      const businessTRN = businesses.find(b => b.business_Id === selectedBusiness.value)?.business_TRN || null;

      const filter = {
        reportDate: formatDateForAPI(today, false),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value,
        businessName: businessName,
        branchName: branchName,
        businessTRN: businessTRN
      };

      const reportData = await getZReportData(filter);
      setZReportData(reportData);
      setShowZReportModal(true);
    } catch (error) {
      console.error("Error fetching Z Report:", error);
      if (error.response?.data?.message && error.response.data.message.includes("Day closing not done")) {
        Swal.fire("Error", "Day closing not done for the day. Please close and try again.", "error");
      } else {
        Swal.fire("Error", "Failed to fetch Z Report data: " + (error.response?.data?.message || error.message), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Print Z Report for a specific row
  const handleRowPrintZReport = async (item) => {
    try {
      setLoading(true);

      // Use the closing date from the row item
      const closingDate = item.closingDate ? new Date(item.closingDate) : null;

      if (!closingDate) {
        Swal.fire("Error", "Invalid closing date for this record", "error");
        setLoading(false);
        return;
      }

      // Use businessId and branchId from the item, or fallback to selected values
      const businessId = item.businessId || selectedBusiness?.value;
      const branchId = item.branchId || selectedBranch?.value;

      if (!businessId || !branchId) {
        Swal.fire("Error", "Business or Branch information is missing", "error");
        setLoading(false);
        return;
      }

      // Get business and branch names from item or selected options
      const businessName = item.businessName || selectedBusiness?.label || businesses.find(b => b.business_Id === businessId)?.business_NameEn || "";
      const branchName = item.branchName || selectedBranch?.label || branches.find(b => b.branch_Id === branchId)?.branch_NameEn || "";
      const businessTRN = businesses.find(b => b.business_Id === businessId)?.business_TRN || null;

      const filter = {
        reportDate: formatDateForAPI(closingDate, false),
        businessId: businessId,
        branchId: branchId,
        businessName: businessName,
        branchName: branchName,
        businessTRN: businessTRN
      };

      const reportData = await getZReportData(filter);
      setZReportData(reportData);
      setShowZReportModal(true);
    } catch (error) {
      console.error("Error fetching Z Report:", error);
      if (error.response?.data?.message && error.response.data.message.includes("Day closing not done")) {
        Swal.fire("Error", "Day closing not done for the selected date. Please close and try again.", "error");
      } else {
        Swal.fire("Error", "Failed to fetch Z Report data: " + (error.response?.data?.message || error.message), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrintZReportPDF = () => {
    if (!zReportPrintRef.current) return;

    const now = new Date();
    const formattedDate = now.toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
    const dateStr = zReportData?.reportDate ? formatDateForAPI(new Date(zReportData.reportDate)).replace(/-/g, "") : "";
    const fileName = `Z_Report_${dateStr}_${formattedDate}`;

    const element = zReportPrintRef.current;

    const options = {
      margin: [10, 10, 10, 10],
      filename: `${fileName}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
      },
      jsPDF: {
        unit: "pt",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf().set(options).from(element).save();
  };

  // Handle export to Excel for a specific row (Daily Closing Summary)
  const handleRowExportToExcel = async (item) => {
    try {
      setLoading(true);
      // Use the closing date from the row item
      const closingDate = item.closingDate ? new Date(item.closingDate) : null;

      if (!closingDate) {
        Swal.fire("Error", "Invalid closing date for this record", "error");
        setLoading(false);
        return;
      }

      // Use businessId and branchId from the item, or fallback to selected values
      const businessId = item.businessId || selectedBusiness?.value;
      const branchId = item.branchId || selectedBranch?.value;

      if (!businessId || !branchId) {
        Swal.fire("Error", "Business or Branch information is missing", "error");
        setLoading(false);
        return;
      }

      const filter = {
        closingDate: formatDateForAPI(closingDate, false),
        businessId: businessId,
        branchId: branchId,
        transactionType: null // null means 'All' tab - same as Daily Closing page
      };

      await exportDailyClosingToExcel(filter);
      Swal.fire("Success", "Daily closing summary exported successfully", "success");
    } catch (error) {
      console.error("Error exporting daily closing summary:", error);
      Swal.fire("Error", "Failed to export daily closing summary: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return formatDateForDisplay(dateString, 'date');
    } catch (error) {
      return "N/A";
    }
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    try {
      return formatDateForDisplay(dateString, 'datetime');
    } catch (error) {
      return "N/A";
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Z Report" breadcrumbItem="POS & Billing" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Z Report</h4>
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={handlePrintZReport}
                      disabled={loading || !selectedBusiness || !selectedBranch}
                    >
                      <Printer size={18} className="me-2" />
                      Print Z Report
                    </Button>
                    <Button
                      color="success"
                      onClick={handleExportToExcel}
                      disabled={loading || !selectedBusiness || !selectedBranch || dayClosings.length === 0}
                    >
                      <Download size={18} className="me-2" />
                      Export to Excel
                    </Button>
                  </div>
                </div>

                {/* Filters */}
                <Card className="mb-4">
                  <CardBody>
                    <Row>
                      <Col md="3">
                        <Label>Company</Label>
                        <Select
                          options={businessOptions}
                          value={selectedBusiness}
                          onChange={(option) => {
                            setSelectedBusiness(option);
                            setSelectedBranch(null);
                            setPage(1);
                          }}
                          isClearable={!userBusinessId}
                          isDisabled={!!userBusinessId}
                          isSearchable
                          placeholder="Select Company"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="3">
                        <Label>Branch</Label>
                        <Select
                          options={branchOptions}
                          value={selectedBranch}
                          onChange={(option) => {
                            setSelectedBranch(option);
                            setPage(1);
                          }}
                          isClearable={!userBranchId}
                          isDisabled={!!userBranchId || !selectedBusiness}
                          isSearchable
                          placeholder="Select Branch"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="2">
                        <Label>From Date</Label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => {
                            setFromDate(date);
                            setPage(1);
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Select From Date"
                          isClearable
                        />
                      </Col>
                      <Col md="2">
                        <Label>To Date</Label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => {
                            setToDate(date);
                            setPage(1);
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Select To Date"
                          isClearable
                          minDate={fromDate}
                        />
                      </Col>
                      <Col md="2">
                        <Label>Type</Label>
                        <Select
                          options={holidayOptions}
                          value={holidayOptions.find(opt => opt.value === holidayFilter)}
                          onChange={(option) => {
                            setHolidayFilter(option.value);
                            setPage(1);
                          }}
                          isSearchable={false}
                          placeholder="All"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Day Closing Records ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Closing Date</th>
                            <th>User</th>
                            <th>Business</th>
                            <th>Branch</th>
                            <th>Cash Total</th>
                            <th>Bank Total</th>
                            <th>Credit Total</th>
                            <th>Tabee Total</th>
                            <th>PC Receipts</th>
                            <th>PC Payments</th>
                            <th>Returns Total</th>
                            <th>Net Total</th>
                            <th>Holiday</th>
                            <th>Notes</th>
                            <th>Created Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="17" className="text-center">
                                <ClipLoader size={20} color="#556ee6" />
                                <span className="ms-2">Loading...</span>
                              </td>
                            </tr>
                          ) : dayClosings.length === 0 ? (
                            <tr>
                              <td colSpan="17" className="text-center">
                                No records found
                              </td>
                            </tr>
                          ) : (
                            dayClosings.map((item, index) => (
                              <tr key={item.dayClosingId}>
                                <td>{startIndex + index + 1}</td>
                                <td>{formatDate(item.closingDate)}</td>
                                <td>{item.userName || 'N/A'}</td>
                                <td>{item.businessName || 'N/A'}</td>
                                <td>{item.branchName || 'N/A'}</td>
                                <td>{formatCurrency(item.cashTotal)}</td>
                                <td>{formatCurrency(item.cashlessTotal)}</td>
                                <td>{formatCurrency(item.creditTotal)}</td>
                                <td>{formatCurrency(item.tabeeTotal || 0)}</td>
                                <td>{formatCurrency(item.pettyCashReceiptsTotal || 0)}</td>
                                <td>{formatCurrency(item.pettyCashPaymentsTotal || 0)}</td>
                                <td>{formatCurrency(item.returnsTotal)}</td>
                                <td>{formatCurrency(item.netTotal)}</td>
                                <td>
                                  <span className={`badge ${item.holiday ? 'bg-warning' : 'bg-success'}`}>
                                    {item.holiday ? 'Holiday' : 'Regular'}
                                  </span>
                                </td>
                                <td>{item.notes || '-'}</td>
                                <td>{formatDateTime(item.createdDate)}</td>
                                <td>
                                  {!item.holiday ? (
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleRowPrintZReport(item)}
                                        disabled={loading}
                                        style={{ minWidth: '80px' }}
                                      >
                                        <Printer size={14} className="me-1" />
                                        Print
                                      </Button>
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handleRowExportToExcel(item)}
                                        disabled={loading}
                                        style={{ minWidth: '80px' }}
                                      >
                                        <Download size={14} className="me-1" />
                                        Export
                                      </Button>
                                    </div>
                                  ) : (
                                    <span>-</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalCount > pageSize && (
                      <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                        style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                        <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                          <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                          Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? startIndex + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{endIndex}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>

                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', minWidth: '60px', textAlign: 'center' }}>
                            Page {page} of {totalPages}
                          </span>

                          <Button
                            color="light"
                            size="sm"
                            disabled={page === totalPages}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Z Report Print Modal */}
      <Modal isOpen={showZReportModal} toggle={() => setShowZReportModal(false)} size="lg">
        <ModalHeader toggle={() => setShowZReportModal(false)}>
          Z Report - {zReportData && formatDateForDisplay(zReportData.reportDate, 'date')}
        </ModalHeader>
        <ModalBody>
          {zReportData && (
            <div ref={zReportPrintRef} className="border p-4">
              <div className="text-center mb-4">
                <h4><strong>{(zReportData.businessBranchInfo?.businessName || 'COMPANY').toUpperCase()}</strong></h4>
                {zReportData.businessBranchInfo?.branchName && <h5>{zReportData.businessBranchInfo.branchName}</h5>}
                <h4>Z REPORT</h4>
                {zReportData.businessBranchInfo?.businessTRN && zReportData.businessBranchInfo.businessTRN.trim() !== '' && (
                  <p className="mb-0"><strong>TRN:</strong> {zReportData.businessBranchInfo.businessTRN}</p>
                )}
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <table className="table table-bordered table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Report Date:</strong> {formatDateForDisplay(zReportData.reportDate, 'date')}</td>
                        <td><strong>Report Time:</strong> {formatDateForDisplay(zReportData.reportDateTime, 'datetime')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary Totals */}
              <div className="row mb-3">
                <div className="col-12">
                  <h5 className="mb-2">Summary</h5>
                  <table className="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Total Sales (Net Total)</strong></td>
                        <td className="text-end"><strong>{formatCurrency(zReportData.dayClosing?.netTotal || 0)}</strong></td>
                      </tr>
                      <tr>
                        <td>Cash Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.cashTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Bank Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.cashlessTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Credit Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.creditTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Tabee Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.tabeeTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Petty Cash Receipts Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.pettyCashReceiptsTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Petty Cash Payments Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.pettyCashPaymentsTotal || 0)}</td>
                      </tr>
                      <tr>
                        <td>Sales Return Total</td>
                        <td className="text-end">{formatCurrency(zReportData.dayClosing?.returnsTotal || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tax and Discount */}
              {(zReportData.taxDiscountTotals?.totalTaxAmount > 0 || zReportData.taxDiscountTotals?.totalDiscountAmount > 0) && (
                <div className="row mb-3">
                  <div className="col-12">
                    <table className="table table-bordered table-sm">
                      <tbody>
                        {zReportData.taxDiscountTotals?.totalTaxAmount > 0 && (
                          <tr>
                            <td><strong>Total Tax</strong></td>
                            <td className="text-end"><strong>{formatCurrency(zReportData.taxDiscountTotals.totalTaxAmount)}</strong></td>
                          </tr>
                        )}
                        {zReportData.taxDiscountTotals?.totalDiscountAmount > 0 && (
                          <tr>
                            <td><strong>Total Discount</strong></td>
                            <td className="text-end"><strong>{formatCurrency(zReportData.taxDiscountTotals.totalDiscountAmount)}</strong></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Opening Cash */}
              {zReportData.openingBalance && (
                <div className="row mb-3">
                  <div className="col-12">
                    <h5 className="mb-2">Opening Cash</h5>
                    <table className="table table-bordered table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Date/Time:</strong> {zReportData.openingBalance.closingDate ? formatDateForDisplay(zReportData.openingBalance.closingDate, 'datetime') : 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Opening Cash:</strong> {formatCurrency(zReportData.openingBalance.cashTotal || 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handlePrintZReportPDF}>
            <Printer size={16} className="me-2" />
            Print PDF
          </Button>
          <Button color="secondary" onClick={() => setShowZReportModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

